import * as React from "react";

import RadioInput from "~/feedback/FormInputs/RadioInput";
import type { DesktopSettings } from "~/lib/desktopApi/desktopSettings";
import type { Provider } from "~/lib/desktopTrayApi/calendarEventsTypes";
import { desktopVersion, versionGt } from "~/lib/version";
import { Divider, Label, Row, SubLabel } from "~/settings/SettingsModal/shared";
import { Checkbox } from "~/ui";
import { Switch } from "~/ui/legacyFellowIndex";
import Selector from "~/ui/selectors/Selector";

const { useCallback, useEffect, useState } = React;

const currentDesktopVersion = desktopVersion();

const notificationTimeOptions: {
	label: string;
	value: string;
	time: number | false;
}[] = [
	{
		label: "10 minutes before",
		value: "10",
		time: 600,
	},
	{
		label: "5 minutes before",
		value: "5",
		time: 300,
	},
	{
		label: "1 minute before",
		value: "1",
		time: 60,
	},
	{
		label: "Do not notify me",
		value: "false",
		time: false,
	},
];
const showCompanionModeForTheseProvidersOptions: {
	label: string;
	value: Provider;
}[] = [
	{
		label: "Google Meet",
		value: "HANGOUTS_MEET",
	},
	{
		label: "Microsoft Teams",
		value: "MS_TEAMS",
	},
	{
		label: "Zoom",
		value: "ZOOM",
	},
	{
		label: "Skype for Business",
		value: "SKYPE_FOR_BUSINESS",
	},
	...(currentDesktopVersion && versionGt(currentDesktopVersion, [1, 4, 0])
		? ([
				{
					label: "Webex",
					value: "WEBEX",
				},
			] as const)
		: []),
	{
		label: "Others",
		value: "UNKNOWN",
	},
];

const settingsAPI = window.electron?.settingsAPI;

/**
 * Acts kinda like useState, but for desktop app settings.
 *
 * @param key - The key of the desired setting in the [[DesktopSettings]] interface.
 */
export function useDesktopSetting<K extends keyof DesktopSettings>(
	key: K,
): { value: DesktopSettings[K]; update: (newValue: DesktopSettings[K]) => void } | false {
	const [value, setValue] = useState(settingsAPI?.get(key));

	// @ts-ignore
	useEffect(() => settingsAPI?.watch(key, setValue), [key]);

	const setter = useCallback(
		(newValue: DesktopSettings[K]) => {
			settingsAPI?.set(key, newValue);
		},
		[key],
	);

	if (!settingsAPI) return false;
	if (value === undefined) return false;

	return {
		value,
		update: setter,
	};
}

function CheckboxRow({ children }: React.PropsWithChildren<{}>) {
	return <label style={{ display: "block", font: "var(--paragraph-4)" }}>{children}</label>;
}

export function DesktopAppSettings() {
	const notificationTime = useDesktopSetting("notificationTime");
	const onNotificationTimeChange = useCallback(
		(opt: { time: number | false } | null) => {
			if (opt === null || notificationTime === false) return;
			notificationTime.update(opt.time);
		},
		[notificationTime],
	);

	// This setting is stored backwards from the UI, so that when we add a new conference provider it starts out enabled by default.
	const disableCompanionModeForTheseProviders = useDesktopSetting("disableCompanionModeForTheseProviders");

	const onToggleNotificationsForProviderChange = useCallback(
		(provider: (typeof showCompanionModeForTheseProvidersOptions)[number], value: boolean) => {
			if (provider === null || disableCompanionModeForTheseProviders === false) return;

			const disableSet = new Set(disableCompanionModeForTheseProviders.value);

			if (value) {
				disableSet.delete(provider.value);
			} else {
				disableSet.add(provider.value);
			}

			disableCompanionModeForTheseProviders.update([...disableSet]);
		},
		[disableCompanionModeForTheseProviders],
	);

	const notificationSound = useDesktopSetting("notificationSound");
	const useCompanionFromTray = useDesktopSetting("useCompanionFromTray");
	const useCompanionFromUpcomingMeetingNotification = useDesktopSetting("useCompanionFromUpcomingMeetingNotification");
	const useCompanionFromNoteHeader = useDesktopSetting("useCompanionFromNoteHeader");
	const notifyForMeetingsWithoutAConferenceCall = useDesktopSetting("notifyForMeetingsWithoutAConferenceCall");
	const onChangeNotifyForMeetingsWithoutAConferenceCall = useCallback(
		(value: string) => {
			if (notifyForMeetingsWithoutAConferenceCall === false) return;
			notifyForMeetingsWithoutAConferenceCall.update(value === "all");
		},
		[notifyForMeetingsWithoutAConferenceCall],
	);

	if (!settingsAPI) return null;

	return (
		<>
			{notificationTime && (
				<>
					<Row>
						<Row.Left>
							<Label>Notify me when a meeting is about to start</Label>
						</Row.Left>
						<Row.Right>
							<Selector
								condensed
								onChange={onNotificationTimeChange}
								options={notificationTimeOptions}
								value={notificationTimeOptions.find(o => o.time === notificationTime.value)}
							/>
						</Row.Right>
					</Row>
					{notificationTime.value !== false && (
						<>
							{notificationSound && (
								<Row>
									<Row.Left>
										<Label>Play notification sound</Label>
									</Row.Left>
									<Row.Right>
										<Switch size="small" checked={notificationSound.value} onCheckedChange={notificationSound.update} />
									</Row.Right>
								</Row>
							)}
							{notifyForMeetingsWithoutAConferenceCall && (
								<>
									<Label>Send notification for</Label>
									<div style={{ margin: "0.5rem 0 1rem", font: "var(--paragraph-5)" }}>
										<RadioInput
											name="notify_for_meetings_without_a_conference_call"
											value={notifyForMeetingsWithoutAConferenceCall.value ? "all" : "only_with_calls"}
											options={[
												{ value: "all", label: "All meetings" },
												{ value: "only_with_calls", label: "Only meetings with a conference link" },
											]}
											onChange={onChangeNotifyForMeetingsWithoutAConferenceCall}
										/>
									</div>
								</>
							)}
						</>
					)}
				</>
			)}
			<Divider />
			{(useCompanionFromTray || useCompanionFromUpcomingMeetingNotification || useCompanionFromNoteHeader) && (
				<>
					<div style={{ margin: "1rem 0" }}>
						<Label>Desktop app companion mode</Label>
						<SubLabel>
							Companion mode is a condensed floating view of the desktop app that accompanies you throughout your
							meetings.
						</SubLabel>
					</div>
					<div style={{ margin: "1rem 0" }}>
						<Label>Automatically open companion mode when joining a meeting from:</Label>
					</div>
					<div
						style={{
							margin: "1rem 0",
							display: "flex",
							flexDirection: "column",
							alignItems: "flex-start",
							gap: "0.5rem",
						}}
					>
						{useCompanionFromTray && (
							<CheckboxRow>
								<Checkbox
									size="0.75rem"
									checked={useCompanionFromTray.value}
									onCheckedChange={useCompanionFromTray.update}
								/>
								&ensp;Desktop tray app
							</CheckboxRow>
						)}
						{useCompanionFromUpcomingMeetingNotification && (
							<CheckboxRow>
								<Checkbox
									size="0.75rem"
									checked={useCompanionFromUpcomingMeetingNotification.value}
									onCheckedChange={useCompanionFromUpcomingMeetingNotification.update}
								/>
								&ensp;Upcoming meeting notification
							</CheckboxRow>
						)}
						{useCompanionFromNoteHeader && (
							<CheckboxRow>
								<Checkbox
									size="0.75rem"
									checked={useCompanionFromNoteHeader.value}
									onCheckedChange={useCompanionFromNoteHeader.update}
								/>
								&ensp;Note header
							</CheckboxRow>
						)}
					</div>
					{disableCompanionModeForTheseProviders && (
						<>
							<Label>Open companion mode when joining a meeting with:</Label>
							<div
								style={{
									margin: "1rem 0",
									display: "flex",
									flexDirection: "column",
									alignItems: "flex-start",
									gap: "0.5rem",
								}}
							>
								{showCompanionModeForTheseProvidersOptions.map(providerOption => (
									<CheckboxRow key={providerOption.value}>
										<Checkbox
											size="0.75rem"
											checked={!disableCompanionModeForTheseProviders.value.includes(providerOption.value)}
											// eslint-disable-next-line fellow/no-inline-disable
											// eslint-disable-next-line react/jsx-no-bind
											onCheckedChange={value => onToggleNotificationsForProviderChange(providerOption, Boolean(value))}
										/>
										&ensp;{providerOption.label}
									</CheckboxRow>
								))}
							</div>
						</>
					)}
				</>
			)}
		</>
	);
}
